import React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Activity, Download } from "lucide-react";
import type { GatewayAlarmNode as GatewayAlarmNodeType, Pathway } from "@/lib/pharmacyFirst/types";

interface GatewayAlarmNodeProps {
  node: GatewayAlarmNodeType;
  isDarkMode?: boolean;
  pathway?: Pathway;
  onAcknowledge: () => void;
  onOpenCalculator?: (calculatorId: string) => void;
  onBack?: () => void;
}

export function GatewayAlarmNode({
  node,
  isDarkMode = false,
  pathway,
  onAcknowledge,
  onOpenCalculator,
  onBack,
}: GatewayAlarmNodeProps) {
  const patientLeafletsSection =
    node.leafletRefs && node.leafletRefs.length > 0 && pathway?.leaflets ? (
      <div
        className={`border rounded-lg p-4 space-y-2 ${
          isDarkMode ? "border-purple-700 bg-purple-950" : "border-purple-200 bg-purple-50"
        }`}
      >
        <h4
          className={`font-semibold text-sm ${
            isDarkMode ? "text-purple-200" : "text-purple-900"
          }`}
        >
          Patient information leaflets
        </h4>
        <div className="space-y-2">
          {node.leafletRefs.map((ref) => {
            const leaflet = pathway.leaflets[ref];
            if (!leaflet) return null;
            const isWelsh = leaflet.language === "cy";
            return (
              <a
                key={ref}
                href={leaflet.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 p-2 rounded text-sm font-medium transition ${
                  isDarkMode
                    ? "hover:bg-purple-900 text-purple-300"
                    : "hover:bg-purple-100 text-purple-700"
                }`}
              >
                <Download className="h-4 w-4" />
                <span>{leaflet.title}</span>
                {isWelsh && (
                  <span
                    className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold ${
                      isDarkMode
                        ? "bg-blue-900 text-blue-200 border border-blue-700"
                        : "bg-blue-100 text-blue-800 border border-blue-300"
                    }`}
                  >
                    Cymraeg
                  </span>
                )}
              </a>
            );
          })}
        </div>
      </div>
    ) : null;

  return (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-5 w-5" />
        <AlertTitle className="text-lg font-bold mt-2">{node.heading}</AlertTitle>
        <AlertDescription className="mt-2 text-base whitespace-pre-line">
          {node.body}
        </AlertDescription>
      </Alert>

      {node.linkedCalculatorId && onOpenCalculator && (
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => onOpenCalculator(node.linkedCalculatorId!)}
        >
          <Activity className="h-4 w-4 mr-2" />
          {node.linkedCalculatorLabel || "Open calculator"}
        </Button>
      )}

      {patientLeafletsSection}

      <div className="flex gap-2">
        {node.action === "close" && node.confirmBeforeClose ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="lg" className="w-full">
                Close Consultation
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Close this consultation?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will end the consultation and cannot be undone. If a red flag
                  was selected by mistake, choose Back instead to return to the
                  checklist.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Go back</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={onAcknowledge}
                >
                  Close consultation
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Button
            onClick={onAcknowledge}
            variant="destructive"
            size="lg"
            className="w-full"
          >
            {node.action === "reset" ? "Acknowledge & Continue" : "Close Consultation"}
          </Button>
        )}
      </div>

      {node.action === "close" && node.confirmBeforeClose && onBack && (
        <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
          Back to checklist
        </Button>
      )}
    </div>
  );
}
